import React from 'react';
import { 
  Container, 
  Grid, 
  Typography, 
  Box, 
  Card, 
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  useTheme,
  alpha,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar
} from '@mui/material';
import { 
  TrendingUp, 
  Inventory, 
  AttachMoney, 
  ShowChart,
  Star,
  Warning,
  CheckCircle,
  TrendingDown,
  Insights,
  Category,
  ShoppingCart,
  Analytics
} from '@mui/icons-material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';
import { useStoreStats, useTopProducts } from '../hooks/useApiWithStore';
import PremiumCard from '../components/ui/PremiumCard';
import PageHeader from '../components/ui/PageHeader';
import { DESIGN_SYSTEM } from '../theme/designSystem';

/**
 * Página de Análisis Comercial de Productos - Completamente Rediseñada
 * Sin espacios vacíos, con información completa y útil
 */
export default function ProductAnalyticsNew() {
  const theme = useTheme();
  const { data: statsData, isLoading: statsLoading } = useStoreStats();
  const { data: topProductsData, isLoading: productsLoading } = useTopProducts();

  // Datos simulados para demostración (reemplazar con datos reales)
  const productPerformanceData = [
    { name: 'Producto A', ventas: 4000, margen: 2400, stock: 24 },
    { name: 'Producto B', ventas: 3000, margen: 1398, stock: 13 },
    { name: 'Producto C', ventas: 2000, margen: 9800, stock: 98 },
    { name: 'Producto D', ventas: 2780, margen: 3908, stock: 48 },
    { name: 'Producto E', ventas: 1890, margen: 4800, stock: 38 }
  ];

  const categoryData = [
    { name: 'Electrónicos', value: 400, color: theme.palette.primary.main },
    { name: 'Ropa', value: 300, color: theme.palette.secondary.main },
    { name: 'Hogar', value: 200, color: theme.palette.success.main },
    { name: 'Deportes', value: 100, color: theme.palette.warning.main }
  ];

  const stockAlerts = [
    { product: 'iPhone 13', stock: 2, status: 'critical', trend: 'down' },
    { product: 'Samsung Galaxy', stock: 5, status: 'warning', trend: 'stable' },
    { product: 'MacBook Pro', stock: 8, status: 'good', trend: 'up' },
    { product: 'AirPods', stock: 15, status: 'good', trend: 'up' }
  ];

  const insights = [
    {
      title: 'Oportunidad de mejora en conversión',
      description: 'Los productos electrónicos tienen 0.5% menos conversión que el promedio',
      type: 'opportunity',
      impact: 'medium'
    },
    {
      title: 'Productos con mejor margen',
      description: 'La categoría Hogar genera 23% más margen que otras categorías',
      type: 'success',
      impact: 'high'
    },
    {
      title: 'Stock crítico detectado',
      description: '3 productos requieren reposición urgente',
      type: 'warning',
      impact: 'high'
    }
  ];

  if (statsLoading || productsLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
          <Box textAlign="center">
            <LinearProgress sx={{ width: 200, mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Cargando análisis de productos...
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Unificado */}
      <PageHeader 
        title={DESIGN_SYSTEM.pageHeaders.products.title}
        subtitle={DESIGN_SYSTEM.pageHeaders.products.subtitle}
        icon="📊"
      />

      {/* Métricas principales */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <PremiumCard
            title="Resumen Ejecutivo"
            icon={<Analytics />}
            exclusive={true}
          >
            <Box textAlign="center" py={2}>
              <Typography variant="h3" fontWeight={700} color="primary.main">
                {statsData?.totalProducts || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Productos Activos
              </Typography>
              <Chip 
                label="Todo en orden" 
                color="success" 
                size="small" 
                sx={{ mt: 1 }}
              />
            </Box>
          </PremiumCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <PremiumCard
            title="Top Performers"
            icon={<Star />}
            badge="Premium"
          >
            <Box textAlign="center" py={2}>
              <Typography variant="h3" fontWeight={700} color="success.main">
                {topProductsData?.data?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Productos con ventas
              </Typography>
              <Typography variant="caption" color="success.main">
                Los productos con ventas aparecerán aquí automáticamente
              </Typography>
            </Box>
          </PremiumCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <PremiumCard
            title="✅ Stock Saludable"
            icon={<CheckCircle />}
            badge="Nuevo"
          >
            <Box textAlign="center" py={2}>
              <Typography variant="h3" fontWeight={700} color="info.main">
                100%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No hay productos con stock crítico
              </Typography>
              <Typography variant="caption" color="info.main">
                Todos los productos tienen niveles de stock adecuados
              </Typography>
            </Box>
          </PremiumCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <PremiumCard
            title="✅ Todo en Orden"
            icon={<CheckCircle />}
            badge="Exclusivo"
          >
            <Box textAlign="center" py={2}>
              <Typography variant="h3" fontWeight={700} color="success.main">
                ✓
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No hay acciones urgentes pendientes
              </Typography>
              <Typography variant="caption" color="success.main">
                Tu tienda está funcionando correctamente
              </Typography>
            </Box>
          </PremiumCard>
        </Grid>
      </Grid>

      {/* Gráficos principales */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={8}>
          <PremiumCard
            title="📈 Rendimiento de Ventas"
            subtitle="Últimos 30 días"
            icon={<ShowChart />}
          >
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={productPerformanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.secondary, 0.1)} />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value, name) => [
                      `$${value.toLocaleString()}`, 
                      name === 'ventas' ? 'Ventas' : 'Margen'
                    ]}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="ventas" 
                    stackId="1"
                    stroke={theme.palette.primary.main}
                    fill={alpha(theme.palette.primary.main, 0.6)}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="margen" 
                    stackId="1"
                    stroke={theme.palette.success.main}
                    fill={alpha(theme.palette.success.main, 0.6)}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </PremiumCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <PremiumCard
            title="📊 Distribución por Categoría"
            icon={<Category />}
          >
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} productos`, 'Cantidad']} />
                </PieChart>
              </ResponsiveContainer>
              <Box mt={2}>
                {categoryData.map((item, index) => (
                  <Box key={index} display="flex" alignItems="center" gap={1} mb={1}>
                    <Box 
                      sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        bgcolor: item.color 
                      }} 
                    />
                    <Typography variant="body2" color="text.secondary">
                      {item.name}: {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </PremiumCard>
        </Grid>
      </Grid>

      {/* Insights Comerciales */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={8}>
          <PremiumCard
            title="🔍 Insights Comerciales"
            subtitle="Análisis en tiempo real"
            icon={<Insights />}
            badge="IA"
          >
            <List>
              {insights.map((insight, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar 
                      sx={{ 
                        bgcolor: insight.type === 'success' ? 'success.main' : 
                                insight.type === 'warning' ? 'warning.main' : 'info.main',
                        width: 40,
                        height: 40
                      }}
                    >
                      {insight.type === 'success' ? <CheckCircle /> : 
                       insight.type === 'warning' ? <Warning /> : <TrendingUp />}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body1" fontWeight={600}>
                          {insight.title}
                        </Typography>
                        <Chip 
                          label={insight.impact} 
                          size="small" 
                          color={insight.impact === 'high' ? 'error' : 'warning'}
                        />
                      </Box>
                    }
                    secondary={insight.description}
                  />
                </ListItem>
              ))}
            </List>
          </PremiumCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <PremiumCard
            title="📦 Análisis de Rentabilidad"
            icon={<AttachMoney />}
            badge="Exclusivo"
          >
            <Box py={2}>
              <Typography variant="h6" fontWeight={600} color="text.primary" gutterBottom>
                Revenue Total
              </Typography>
              <Typography variant="h4" fontWeight={700} color="primary.main" gutterBottom>
                ${statsData?.totalSales?.toLocaleString() || '0'}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Ganancia Estimada
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    ${((statsData?.totalSales || 0) * 0.25).toLocaleString()}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={25} 
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Margen: 25%
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Mejor Categoría
                </Typography>
                <Typography variant="body1" fontWeight={600} color="success.main">
                  N/A
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Análisis disponible con más datos
                </Typography>
              </Box>
            </Box>
          </PremiumCard>
        </Grid>
      </Grid>

      {/* Recomendaciones IA */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PremiumCard
            title="🤖 Recomendaciones IA"
            subtitle="Generadas por IA avanzada"
            icon={<Insights />}
            badge="Premium"
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box 
                  sx={{ 
                    p: 3, 
                    borderRadius: 2, 
                    background: alpha(theme.palette.success.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.16)}`
                  }}
                >
                  <Typography variant="h6" fontWeight={600} color="success.main" gutterBottom>
                    💡 Implementar
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Análisis de tiempo real • Implementación guiada paso a paso
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box 
                  sx={{ 
                    p: 3, 
                    borderRadius: 2, 
                    background: alpha(theme.palette.info.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.16)}`
                  }}
                >
                  <Typography variant="h6" fontWeight={600} color="info.main" gutterBottom>
                    📊 Mejorar
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Análisis de tendencias • Optimización automática
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box 
                  sx={{ 
                    p: 3, 
                    borderRadius: 2, 
                    background: alpha(theme.palette.warning.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.16)}`
                  }}
                >
                  <Typography variant="h6" fontWeight={600} color="warning.main" gutterBottom>
                    🚀 Implementar
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Recomendaciones generadas por IA • Análisis en tiempo real • Implementación guiada paso a paso
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </PremiumCard>
        </Grid>
      </Grid>
    </Container>
  );
}
